import { DEFAULT_PAGE_SIZE, FORBIDDEN_ERROR } from 'constants/config';
import { OFFICES } from 'constants/permissionConstants';
import { Spin, Table, Select, Pagination, TablePaginationConfig, Button, Menu, Alert } from 'antd';
import { FilterValue } from 'antd/lib/table/interface';
import withPermission from 'components/HOCs/withPermission';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GetOfficeRequest, OfficeType, PaginatedResonse } from 'services/office';

// Icon import
import { ReactComponent as OfficeIcon } from 'resources/icons/Group 2116.svg';
import { ReactComponent as EditIcon } from 'resources/icons/Group 524.svg';
import { ReactComponent as DeactivateIcon } from 'resources/icons/person-x-fill.svg';
import { FaUserCheck } from 'react-icons/fa';
import localStorageService from 'helpers/localStorageService';
import { Permission } from 'types/common';
import { UserDetail } from 'types/user';
import OfficeTableColumns from './OfficeTableColumns';

// ADD OFFICE COMPONENT - STARTS HERE
type LinkDisable = {
  disabled: boolean;
};
type EditCompanyProps = {
  disabled: boolean;
  linkAddress: string | undefined;
};

const AddOfficeLink = (props: LinkDisable) => {
  const { disabled } = props;
  return disabled ? (
    <>
      <Button className="tbl-top-btn" disabled>
        <OfficeIcon className="tbl-btn-icon officeIcon" /> Add Office
      </Button>
    </>
  ) : (
    <Link to="/company/new-office-location">
      <Button className="tbl-top-btn">
        <OfficeIcon className="tbl-btn-icon officeIcon" /> Add Office
      </Button>
    </Link>
  );
};

const EditCompanyLink = (props: EditCompanyProps) => {
  const { disabled, linkAddress } = props;
  return disabled ? (
    <>
      <div className="editWrapper disabledEdit">
        <div className="wrapper">
          <EditIcon className="editIcon" />
          <span>Edit</span>
        </div>
      </div>
    </>
  ) : (
    <Link to={`company/${linkAddress}`}>
      <div className="editWrapper">
        <div className="wrapper">
          <EditIcon className="editIcon" />
          <span>Edit</span>
        </div>
      </div>
    </Link>
  );
};

export const AddOfficeLinkWithPermission = withPermission(AddOfficeLink);
export const EditCompanyLinkWithPermission = withPermission(EditCompanyLink);
// ADD OFFICE COMPONENT - ENDS HERE

// LIST OFFICE COMPONENT - STARTS HERE
const { Option } = Select;

type OfficeTableProps = {
  // native to component props
  data: PaginatedResonse | undefined;
  filters: GetOfficeRequest;
  isLoading: boolean;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  handleTableChange(
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: any,
  ): void;
  handlePageChange: (page: number, pageSize: number) => void;
  openUpdateOfficeStatusModal(id: string, name: string, status: boolean): void;
  setFilters: Dispatch<SetStateAction<GetOfficeRequest>>;

  // permission relevant props
  disabled: boolean;

  editPermission: boolean;
  setEditPermission: Dispatch<boolean>;

  changeStatusPermission: boolean;
  setChangeStatusPermission: Dispatch<boolean>;
};

const OfficeTable = (props: OfficeTableProps) => {
  const [menuPermission, setMenuPermission] = useState(true);
  const menuPermissions = [OFFICES.EDIT, OFFICES.ACTIVATE];
  useEffect(() => {
    //  Get user details from local storage
    const userInfo: UserDetail = localStorageService.get('userInfo');
    const userPermissions: Permission[] | undefined = userInfo?.role?.permissions;

    //  Compute user access

    setMenuPermission(
      menuPermissions.some(key => userPermissions?.find(item => item.permissionId === key && item.isActive === true)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data,
    filters,
    isLoading,
    handleTableChange,
    handlePageChange,
    openUpdateOfficeStatusModal,
    setFilters,
    disabled,
    editPermission,
    setEditPermission,
    changeStatusPermission,
    setChangeStatusPermission,
  } = props;

  return (
    <Spin spinning={isLoading}>
      {disabled ? (
        <Alert message={FORBIDDEN_ERROR} type="error" />
      ) : (
        <div>
          <div>
            <Table<OfficeType>
              rowKey={record => `user-${record.id}`}
              rowClassName={record => (record.isActive ? '' : 'rowInactiveColor')}
              columns={OfficeTableColumns({
                menuPermission,
                openUpdateOfficeStatusModal,
                editPermission,
                setEditPermission,
                changeStatusPermission,
                setChangeStatusPermission,
              })}
              pagination={false}
              dataSource={data?.data || []}
              size="small"
              onChange={handleTableChange}
              showSorterTooltip={false}
            />
          </div>
          <div className="pagination">
            <div className="cutomPaginationStyling">
              Showing
              <Select
                className="customPageSelector"
                defaultValue="10/page"
                onChange={e => {
                  const total = data?.total || 0;
                  const LastPage = Math.ceil(total / +e);
                  const currentPage = data?.pageNo || 1;
                  setFilters({
                    ...filters,
                    limit: +e,
                    pageNo: currentPage > LastPage ? LastPage : currentPage,
                  });
                }}>
                <Option value="10">10/page</Option>
                <Option value="20">20/page</Option>
                <Option value="30">30/page</Option>
              </Select>
              of {data?.total || 0} entries
            </div>
            <Pagination
              showLessItems
              showSizeChanger={false}
              pageSize={filters?.limit || DEFAULT_PAGE_SIZE}
              total={data?.total || 0}
              current={data?.pageNo || 1}
              onChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </Spin>
  );
};

export const OfficeTableWithPermission = withPermission(OfficeTable);

// LIST OFFICE COMPONENT - ENDS HERE

// EDIT OFFICE COMPONENT - STARTS HERE

type EditLinkProps = {
  disabled: boolean;
  record: OfficeType;
  text: string;
};
const EditLink = (props: EditLinkProps) => {
  const { disabled, record, text } = props;
  if (disabled || !record.isActive) {
    return null;
  }
  return (
    <Link to={`/company/${record.name}/${text}`}>
      <div className="action_DropDown">
        <EditIcon className="dropDown_icon" />
        <span className="dropDown_list_text">Edit Office</span>
      </div>
    </Link>
  );
};
export const EditLinkWithPermission = withPermission(EditLink);

// EDIT OFFICE COMPONENT - ENDS HERE

// CHANGE STATUS OFFICE COMPONENT - STARTS HERE

type ChangeStatusLinkProps = {
  disabled: boolean;
  record: OfficeType;
  text: string;
  openUpdateOfficeStatusModal(id: string, firstName: string, status: boolean): void;
};
const ChangeStatusLink = (props: ChangeStatusLinkProps) => {
  const { disabled, record, text, openUpdateOfficeStatusModal } = props;

  return (
    <Menu.Item
      key={`deactivate-${record.id}`}
      className="action-menu"
      onClick={disabled ? () => {} : () => openUpdateOfficeStatusModal(text, record.name, !record.isActive)}>
      {disabled ? null : (
        <div className="action_DropDown">
          {record.isActive ? (
            <DeactivateIcon className="dropDown_icon" />
          ) : (
            <FaUserCheck size={18} className="dropDown_icon" />
          )}
          <span className="dropDown_list_text">{record.isActive ? 'Deactivate' : 'Activate'}</span>
        </div>
      )}
    </Menu.Item>
  );
};

export const ChangeStatusLinkWithPermission = withPermission(ChangeStatusLink);

// CHANGE STATUS OFFICE COMPONENT - ENDS HERE
